import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Alert,
  Pagination,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  IosShare as ShareIcon,
} from "@mui/icons-material";
import { useShareTriviaGameMutation } from "../../store/api/mlbApi";
import TriviaShareCard from "./TriviaShareCard";
import { getLetterGrade } from "../../utils/triviaArcade";
import { buildShareCardHeadline, buildShareCardTitle } from "../../utils/triviaShareCard";
import { SPORT_BY_ID } from "../../constants/sportWorkspace";

const TriviaGameHistory = ({
  gameHistory,
  isLoading,
  compact = false,
  pagination,
  onPageChange,
}) => {
  const [sharing, setSharing] = useState(null);
  const [shareError, setShareError] = useState(null);
  const [shareGame, { isLoading: isSharing }] = useShareTriviaGameMutation();
  if (isLoading) {
    return (
      <Box>
        <LinearProgress />
        <Typography variant="body1" sx={{ mt: 2, textAlign: "center" }}>
          Loading game history...
        </Typography>
      </Box>
    );
  }

  if (!gameHistory || gameHistory.length === 0) {
    return (
      <Alert severity="info">
        <Typography variant="h6" sx={{ mb: 1 }}>
          No games played yet!
        </Typography>
        <Typography>
          Start playing trivia games to see your history here.
        </Typography>
      </Alert>
    );
  }

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "easy": return "success";
      case "medium": return "warning";
      case "hard": return "error";
      default: return "default";
    }
  };

  const getCategoryDisplayName = (category) => {
    switch (category) {
      case "career_hitting": return "Career Hitting";
      case "career_pitching": return "Career Pitching";
      case "team_history": return "Team History";
      case "all_time_greats": return "All-Time Greats";
      default: return category;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (milliseconds) => {
    const seconds = Math.round(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const openShare = async (game) => {
    setShareError(null);
    if (game.shareToken) {
      setSharing(game);
      return;
    }
    try {
      const result = await shareGame(game.sessionId).unwrap();
      setSharing({ ...game, ...result.result });
    } catch (error) {
      setShareError(error?.data?.error || error?.message || "Could not share this game.");
    }
  };

  const sharingLabel = sharing
    ? buildShareCardHeadline(SPORT_BY_ID[sharing.sport]?.label, sharing.gameMode)
    : "";

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Game History ({gameHistory.length} games)
      </Typography>

      <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Difficulty</TableCell>
              <TableCell align="right">Score</TableCell>
              <TableCell align="right">Accuracy</TableCell>
              <TableCell align="right">Duration</TableCell>
              <TableCell align="right">XP</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {gameHistory.map((game, index) => (
              <TableRow key={game.sessionId} hover>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDate(game.endTime)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {SPORT_BY_ID[game.sport]?.label || "MLB"} ·{" "}
                      {buildShareCardTitle(game.gameMode)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {getCategoryDisplayName(game.category)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={game.difficulty}
                    size="small"
                    color={getDifficultyColor(game.difficulty)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                    <TrophyIcon sx={{ fontSize: 16, color: "primary.main" }} />
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {game.score}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                    <CheckIcon 
                      sx={{ 
                        fontSize: 16, 
                        color: game.accuracy > 0.8 ? "success.main" : 
                               game.accuracy > 0.6 ? "warning.main" : "error.main"
                      }} 
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: game.accuracy > 0.8 ? "success.main" : 
                               game.accuracy > 0.6 ? "warning.main" : "error.main",
                        fontWeight: 500
                      }}
                    >
                      {Math.round(game.accuracy * 100)}%
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                    <ScheduleIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2">
                      {formatDuration(game.totalTime)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={`+${game.experiencePoints} XP`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right" sx={{ width: 56 }}>
                  <IconButton
                    size="small"
                    aria-label="Share"
                    onClick={() => openShare(game)}
                    disabled={isSharing}
                  >
                    <ShareIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {shareError ? (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setShareError(null)}>
          {shareError}
        </Alert>
      ) : null}

      {pagination?.totalPages > 1 ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            page={pagination.currentPage || 1}
            count={pagination.totalPages}
            onChange={(_event, page) => onPageChange?.(page)}
          />
        </Box>
      ) : null}

      <Dialog open={Boolean(sharing)} onClose={() => setSharing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{sharingLabel}</DialogTitle>
        <DialogContent>
          {sharing ? (
            <TriviaShareCard
              mode={sharing.gameMode || "quick"}
              grade={getLetterGrade(sharing.accuracy || 0)}
              correct={sharing.correctAnswers || 0}
              total={sharing.totalQuestions || 0}
              score={sharing.score || 0}
              shareToken={sharing.shareToken}
              username={sharing.username}
              sportLabel={SPORT_BY_ID[sharing.sport]?.label}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Summary Stats */}
      <Box sx={{ mt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                {gameHistory.reduce((sum, game) => sum + game.score, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Score
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                {Math.round(
                  gameHistory.reduce((sum, game) => sum + game.accuracy, 0) / gameHistory.length * 100
                )}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Accuracy
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h6" color="warning.main" sx={{ fontWeight: 600 }}>
                {Math.max(...gameHistory.map(game => game.score))}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Best Score
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h6" color="info.main" sx={{ fontWeight: 600 }}>
                {gameHistory.reduce((sum, game) => sum + game.experiencePoints, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total XP Earned
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default TriviaGameHistory;
